import * as angular from "angular"
import { handleApiError } from "../../error"
import { getHoldingFilter, IHolding } from "../../holding"
import { IHoldingService } from "../../holding/types"
import { showDeleteCancel, showWarning } from "../../react/msgbox"
import { IOmniaShell } from "../../shell"
import { IOmniaSession, IUibModalScope, LocaleString } from "../../types"
import { RoleId } from "../../user"
import { defineDirective } from "../../utils/angular"
import { ReactHoldingEditDialog } from "@omnia/shared/holding/edit/HoldingEditDialog"

/** @internal */
export class EditUserHoldingsCtrl {

    static create(): ng.IDirective {

        return defineDirective(EditUserHoldingsCtrl, "shared/user/holdings/holdings.html", {});

    };

    private $scope: IUibModalScope;

    private _search: string;
    private _holdings: IHolding[];

    private _canManageHoldings: boolean;
    private _canExportHoldings: boolean;

    private form: ng.IFormController;
    private holdingSaved: boolean;
    private isEditingHolding: boolean;
    private selectedHolding: IHolding;
    private holding: IHolding;

    static $inject = ["$scope", "$timeout", "OmniaShell", "OmniaSession", "OmniaHoldingService"];

    constructor($scope: ng.IScope, private $timeout: ng.ITimeoutService, private shell: IOmniaShell, private session: IOmniaSession, private holdingSvc: IHoldingService) {

        this.$scope = <IUibModalScope>$scope.$parent;

    }

    $onInit() {
        const { permissions } = this.session
        const role = this.session.currentUser.Role
        this._canExportHoldings = role <= RoleId.Admin
        this._canManageHoldings = Boolean(permissions.holdings.write)
        this.updateHoldings()
    }

    get title(): string {
        const { holding: e } = this
        return !e
            ? "Manage holdings" as LocaleString
            : !e.HoldingId
                ? "Create holding" as LocaleString
                : e.TradingName?.length 
                    ? `Edit holding - ${e.TradingName}` 
                    : `Edit holding`
    }

    get icon(): string {
        return !this.holding ? "holding" : !this.holding.HoldingId ? "addnew" : "edit";
    }

    get search(): string {
        return this._search;
    }

    set search(value: string) {
        this._search = value;
        this.updateHoldings();
    }

    get holdings() {
        return this._holdings;
    }

    updateHoldings() {

        if (this.session.hasFixedHoldings()) {

            let holdings = this.session.getHoldings();

            if (this.search) {
                let filter = getHoldingFilter(this.search);
                holdings = holdings.filter(filter);
            }

            holdings = holdings.sort((h1, h2) => h1.TradingName.localeCompare(h2.TradingName));

            this._holdings = holdings;

        }
        else {

            this.session.queryHoldings(this.search).then(h => this._holdings = h)

        }

    }

    filterHolding(text: string): ((item: IHolding) => boolean) {
        return getHoldingFilter(text);
    }

    get canUserEditHoldings(): boolean {
        return this._canManageHoldings;
    }

    editHolding(holding: IHolding) {
        
        this.selectedHolding = null;
        this.holding = angular.merge({}, holding);

        const showDialogPromise = this.shell.showComponentAsDialog(
                    ReactHoldingEditDialog, 
                    {
                        holding: holding,
                    }, 
                    { 
                        backdrop: "static" 
                    });
        this.isEditingHolding = true;
        let saved = false;

        return showDialogPromise
            .then((result) => { 
                if (result) {
                    // holding has been saved

                    saved = true

                    this.isEditingHolding = false;
                    this.holding = null;

                    this.holdingSaved = true
                    this.$timeout(() => this.holdingSaved = false, 10000)

                    return this.session.refreshHoldings().then(() => this.updateHoldings())
                }
                else {
                    // holding has not been saved

                    this.holding = null;
                    this.isEditingHolding = false;
                }
            })
            .catch(e => {

                // 220316 - There have been reports of errors occurring when creating,
                // with no traces on the backend
                // This means that those errors probably occur during the "then()" branch above
                // Pending a better solution, we now ignore any error if the holding has been correctly saved

                console.error(e)

                if (!saved) {
                    return handleApiError(this.shell, e)
                }

            })
    }

    canUserDeleteHolding(holding: IHolding): boolean {

        if (holding.HoldingId === this.session.holdingId) {
            return false;
        }

        return this._canManageHoldings;

    }

    get canUserExportHolding(): boolean {
        return this._canExportHoldings;
    }

    exportHolding(holding: IHolding) {
        this.holdingSvc.exportHolding(holding);
    }

    deleteHolding(holding: IHolding) {

        const titleDeleteHolding = "Delete holding"
        const titleUnableToDelete = "Unable to delete holding"
        const msgDeleteConfirmation = "Click 'Delete' to delete this holding.<br/>Please note that this operation is irreversible.";
        const msgCannotDeleteHoldingNotEmpty = "This holding is not empty. You can only delete empty holdings.";

        this.holdingSvc.isHoldingEmpty(holding.HoldingId).then(r => {
            if (!r.data) {
                showWarning(msgCannotDeleteHoldingNotEmpty, titleUnableToDelete)
            } else {
                showDeleteCancel(msgDeleteConfirmation, titleDeleteHolding).then((r) => {
                    if (r) {
                        this.holdingSvc
                            .deleteHolding(holding.HoldingId)
                            .then(() => this.session.refreshHoldings())
                            .then(() => this.updateHoldings())
                    }
                })
            }
        });
    }

    close() {
        this.$scope.$close();
    }

}
